/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import { onSwiftRefresh } from "@src/components/modules/TemplateModule/TopBar/refresh";
import { useNavigate } from "react-router";

import MainTemplate from "@src/components/modules/TemplateModule/MainTemplate";
import Navigation from "@src/components/modules/NavigationModule/Navigation";
import FilterList from "@src/components/ui/Lists/FilterList";
import PageHeader from "@src/components/smart/PageHeader";
import EndpointListItem from "@src/components/modules/EndpointModule/EndpointListItem";
import AlertModal from "@src/components/ui/AlertModal";
import Modal from "@src/components/ui/Modal";
import ChooseProvider from "@src/components/modules/EndpointModule/ChooseProvider";
import EndpointModal from "@src/components/modules/EndpointModule/EndpointModal";
import type { Endpoint as EndpointType } from "@src/@types/Endpoint";

import projectStore from "@src/stores/ProjectStore";
import userStore from "@src/stores/UserStore";
import endpointStore from "@src/stores/EndpointStore";
import deploymentStore from "@src/stores/DeploymentStore";
import transferStore from "@src/stores/TransferStore";
import providerStore from "@src/stores/ProviderStore";
import EndpointDuplicateOptions from "@src/components/modules/EndpointModule/EndpointDuplicateOptions";

import configLoader from "@src/utils/Config";
import { ThemePalette } from "@src/components/Theme";
import { ProviderTypes } from "@src/@types/Providers";
import regionStore from "@src/stores/RegionStore";
import EndpointSource from "@src/sources/EndpointSource";
import {
  ENDPOINT_COLUMNS,
  endpointRegion,
} from "@src/components/modules/EndpointModule/EndpointListItem/EndpointListItem";
import type { EndpointCheck } from "@src/components/modules/EndpointModule/EndpointListItem/EndpointListItem";
import { providerLabel } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import {
  ClearFiltersButton,
  FilterSelect,
  ToolbarSearch,
  matchesSearch,
  useListQuery,
} from "@src/components/ui/Lists/ListToolbar";
import type { ListQuery } from "@src/components/ui/Lists/ListToolbar";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";
import { Cloud } from "lucide-react";

const CHECKS_KEY = "swift.endpointChecks";
const loadChecks = (): { [id: string]: EndpointCheck } => {
  try {
    return JSON.parse(window.localStorage.getItem(CHECKS_KEY) || "{}") || {};
  } catch (e) {
    return {};
  }
};
const saveChecks = (checks: { [id: string]: EndpointCheck }) => {
  try {
    window.localStorage.setItem(CHECKS_KEY, JSON.stringify(checks));
  } catch (e) {
    // private mode / storage blocked: the checks only last for this page
  }
};
const FILTER_KEYS = ["q", "provider", "region", "usage"];

const Wrapper = styled.div<any>``;

type State = {
  selectedEndpoints: EndpointType[];
  showChooseProviderModal: boolean;
  showEndpointModal: boolean;
  providerType: ProviderTypes | null;
  showEndpointsInUseModal: boolean;
  modalIsOpen: boolean;
  showDeleteEndpointsModal: boolean;
  showDuplicateModal: boolean;
  duplicating: boolean;
  uploadedEndpoint: EndpointType | null;
  multiValidating: boolean;
  checks: { [id: string]: EndpointCheck };
};

type Props = {
  onNavigate: (path: string) => void;
  query: ListQuery;
};

@observer
class EndpointsPage extends React.Component<Props, State> {
  state: State = {
    showChooseProviderModal: false,
    showEndpointModal: false,
    providerType: null,
    showEndpointsInUseModal: false,
    modalIsOpen: false,
    showDuplicateModal: false,
    duplicating: false,
    showDeleteEndpointsModal: false,
    selectedEndpoints: [],
    uploadedEndpoint: null,
    multiValidating: false,
    checks: loadChecks(),
  };

  pollTimeout = 0;

  stopPolling = false;

  offSwiftRefresh?: () => void;

  componentDidMount() {
    // The top bar's Refresh reloads this page's data.
    this.offSwiftRefresh = onSwiftRefresh(() => {
      this.handleReloadButtonClick();
    });
    document.title = "Cloud endpoints · SwiftIQ";

    projectStore.getProjects();
    if (!regionStore.regions.length) {
      regionStore.getRegions();
    }

    this.stopPolling = false;
    this.pollData(true);
  }

  componentWillUnmount() {
    this.offSwiftRefresh?.();
    clearTimeout(this.pollTimeout);
    this.stopPolling = true;
  }

  get filtersActive() {
    return FILTER_KEYS.some(k => this.props.query.get(k));
  }

  clearFilters() {
    this.props.query.set({ q: null, provider: null, region: null, usage: null });
  }

  regionName(id: string) {
    return regionStore.regions.find(r => r.id === id)?.name;
  }

  isInUse(endpoint: EndpointType) {
    const usage = this.getEndpointUsage(endpoint.id);
    return usage.migrationsCount + usage.replicasCount > 0;
  }

  filteredEndpoints(): EndpointType[] {
    const q = this.props.query;
    const text = q.get("q");
    const providers = q.getList("provider");
    const region = q.get("region");
    const usage = q.get("usage");
    return endpointStore.endpoints
      .filter(e => {
        if (providers.length && !providers.includes(e.type)) return false;
        if (region && !endpointRegion(e, id => this.regionName(id)).split(", ").includes(region)) return false;
        if (usage === "used" && !this.isInUse(e)) return false;
        if (usage === "unused" && this.isInUse(e)) return false;
        if (text) {
          return matchesSearch(text, [
            e.name,
            e.description,
            e.id,
            providerLabel(e.type),
            endpointRegion(e, id => this.regionName(id)),
          ]);
        }
        return true;
      })
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  renderToolbar() {
    const q = this.props.query;
    const all = endpointStore.endpoints;
    const providers = [...new Set(all.map(e => e.type))]
      .map(t => ({ value: t, label: providerLabel(t), count: all.filter(e => e.type === t).length }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const regions = [
      ...new Set(
        all
          .map(e => endpointRegion(e, id => this.regionName(id)))
          .join(", ")
          .split(", ")
          .filter(Boolean),
      ),
    ]
      .sort()
      .map(r => ({ value: r, label: r }));
    return (
      <>
        <ToolbarSearch
          value={q.get("q")}
          placeholder="Search name, description or region"
          onChange={v => q.set({ q: v })}
        />
        <FilterSelect
          label="Provider"
          multiple
          value={q.getList("provider")}
          onChange={v => q.set({ provider: v })}
          options={providers}
        />
        <FilterSelect
          label="Region"
          value={q.get("region")}
          onChange={v => q.set({ region: v })}
          options={regions}
          allLabel="Any"
        />
        <FilterSelect
          label="Usage"
          value={q.get("usage")}
          onChange={v => q.set({ usage: v })}
          options={[
            { value: "used", label: "In use", count: all.filter(e => this.isInUse(e)).length },
            { value: "unused", label: "Unused", count: all.filter(e => !this.isInUse(e)).length },
          ]}
          allLabel="Any"
        />
        {this.filtersActive ? <ClearFiltersButton onClick={() => this.clearFilters()} /> : null}
      </>
    );
  }

  setCheck(id: string, check: EndpointCheck | null) {
    this.setState(
      prev => {
        const checks = { ...prev.checks };
        if (check) checks[id] = check;
        else delete checks[id];
        return { checks };
      },
      () => {
        const persisted: { [id: string]: EndpointCheck } = {};
        Object.keys(this.state.checks).forEach(k => {
          if (this.state.checks[k].state !== "checking") persisted[k] = this.state.checks[k];
        });
        saveChecks(persisted);
      },
    );
  }

  async validateEndpoint(endpoint: EndpointType) {
    this.setCheck(endpoint.id, { state: "checking" });
    try {
      const result = await EndpointSource.validate(endpoint);
      this.setCheck(endpoint.id, {
        state: result.valid ? "valid" : "failed",
        message: result.message,
        at: new Date().toISOString(),
      });
    } catch (err: any) {
      this.setCheck(endpoint.id, {
        state: "failed",
        message: err?.data?.description || err?.message || "Validation failed",
        at: new Date().toISOString(),
      });
    }
  }

  rowActions(endpoint: EndpointType): DropdownAction[] {
    return [
      { label: "Open", action: () => this.handleItemClick(endpoint) },
      {
        label: "Validate",
        disabled: this.state.checks[endpoint.id]?.state === "checking",
        action: () => {
          this.validateEndpoint(endpoint);
        },
      },
      {
        label: "Duplicate…",
        action: () =>
          this.setState({ selectedEndpoints: [endpoint], showDuplicateModal: true, modalIsOpen: true }),
      },
      { label: "Download .endpoint file", action: () => endpointStore.exportToJson(endpoint) },
      {
        label: "Delete…",
        color: ThemePalette.alert,
        action: () => this.setState({ selectedEndpoints: [endpoint] }, () => this.handleDeleteAction()),
      },
    ];
  }

  getEndpointUsage(endpointId: string) {
    const replicasCount = transferStore.transfers.filter(
      r =>
        (r.origin_endpoint_id === endpointId ||
          r.destination_endpoint_id === endpointId) &&
        r.scenario === "replica",
    ).length;
    const migrationsCount = transferStore.transfers.filter(
      r =>
        (r.origin_endpoint_id === endpointId ||
          r.destination_endpoint_id === endpointId) &&
        r.scenario === "live_migration",
    ).length;

    return { migrationsCount, replicasCount };
  }

  handleProjectChange() {
    endpointStore.getEndpoints({ showLoading: true });
    deploymentStore.getDeployments();
    transferStore.getTransfers();
  }

  handleReloadButtonClick() {
    projectStore.getProjects();
    endpointStore.getEndpoints({ showLoading: true });
    deploymentStore.getDeployments();
    transferStore.getTransfers();
  }

  handleItemClick(item: EndpointType) {
    this.props.onNavigate(`/endpoints/${item.id}`);
  }

  async duplicate(projectId: string) {
    this.setState({ modalIsOpen: false, duplicating: true });

    const shouldSwitchProject =
      projectId !==
      (userStore.loggedUser ? userStore.loggedUser.project.id : "");
    const endpoints = endpointStore.endpoints.filter(e =>
      this.state.selectedEndpoints.find(se => se.id === e.id),
    );

    await endpointStore.duplicate({
      shouldSwitchProject,
      endpoints,
      onSwitchProject: async () => {
        await userStore.switchProject(projectId);
        this.handleProjectChange();
      },
    });
    this.pollData(true);
    this.setState({ showDuplicateModal: false, duplicating: false });
  }

  deleteSelectedEndpoints() {
    this.state.selectedEndpoints.forEach(endpoint => {
      endpointStore.delete(endpoint);
    });
    this.setState({ showDeleteEndpointsModal: false });
  }

  handleEmptyListButtonClick() {
    providerStore.loadProviders();
    regionStore.getRegions();
    this.setState({ showChooseProviderModal: true });
  }

  handleRemoveEndpoint(endpoint: EndpointType) {
    endpointStore.delete(endpoint);
  }

  async handleValidateMultipleEndpoints(endpoints: EndpointType[]) {
    this.setState({ multiValidating: true });
    const addedEndpoints = await endpointStore.addMultiple(endpoints);
    await endpointStore.validateMultiple(addedEndpoints);
    this.setState({ multiValidating: false });
  }

  handleResetValidation() {
    endpointStore.resetMultiValidation();
  }

  handleCloseChooseProviderModal() {
    this.setState({ showChooseProviderModal: false });
  }

  handleProviderClick(providerType: ProviderTypes) {
    this.setState({
      showChooseProviderModal: false,
      showEndpointModal: true,
      uploadedEndpoint: null,
      providerType,
    });
  }

  handleUploadEndpoint(endpoint: EndpointType) {
    endpointStore.setConnectionInfo(endpoint.connection_info);
    this.setState({
      showChooseProviderModal: false,
      showEndpointModal: true,
      providerType: endpoint.type,
      uploadedEndpoint: endpoint,
    });
  }

  handleCloseEndpointModal() {
    this.setState({ showEndpointModal: false });
  }

  handleModalOpen() {
    this.setState({ modalIsOpen: true });
  }

  handleModalClose() {
    this.setState({ modalIsOpen: false }, () => {
      this.pollData();
    });
  }

  handleExportToJson() {
    if (this.state.selectedEndpoints.length === 1) {
      endpointStore.exportToJson(this.state.selectedEndpoints[0]);
    } else {
      endpointStore.exportToZip(this.state.selectedEndpoints);
    }
  }

  handleDeleteAction() {
    const endpointsInUse = this.state.selectedEndpoints.filter(endpoint => {
      const endpointUsage = this.getEndpointUsage(endpoint.id);
      return (
        endpointUsage.migrationsCount > 0 || endpointUsage.replicasCount > 0
      );
    });

    if (endpointsInUse.length > 0) {
      this.setState({ showEndpointsInUseModal: true });
    } else {
      this.setState({ showDeleteEndpointsModal: true });
    }
  }

  async pollData(showLoading = false) {
    if (this.state.modalIsOpen || this.stopPolling) {
      return;
    }

    await Promise.all([
      endpointStore.getEndpoints({ showLoading, skipLog: true }),
      deploymentStore.getDeployments({ skipLog: true }),
      transferStore.getTransfers({ skipLog: true }),
    ]);
    this.pollTimeout = window.setTimeout(() => {
      this.pollData();
    }, configLoader.config.requestPollTimeout);
  }

  render() {
    const items: any = this.filteredEndpoints();
    const selectedProjectId = userStore.loggedUser
      ? userStore.loggedUser.project.id
      : "";
    const BulkActions = [
      {
        label: "Validate",
        action: () => {
          this.state.selectedEndpoints.forEach(e => {
            this.validateEndpoint(e);
          });
        },
      },
      {
        label: "Duplicate…",
        action: () => {
          this.setState({ showDuplicateModal: true, modalIsOpen: true });
        },
      },
      {
        label: "Download .endpoint files",
        action: () => {
          this.handleExportToJson();
        },
      },
      {
        label: "Delete…",
        color: ThemePalette.alert,
        action: () => {
          this.handleDeleteAction();
        },
      },
    ];

    return (
      <Wrapper>
        <MainTemplate
          navigationComponent={<Navigation currentPage="endpoints" />}
          listComponent={
            <FilterList
              toolbar={this.renderToolbar()}
              filtersActive={this.filtersActive}
              onClearFilters={() => this.clearFilters()}
              columns={ENDPOINT_COLUMNS}
              selectionLabel="endpoint"
              loading={endpointStore.loading}
              items={items}
              onItemClick={item => {
                const anyItem: any = item;
                const endpoint: EndpointType = anyItem;
                this.handleItemClick(endpoint);
              }}
              dropdownActions={BulkActions}
              onSelectedItemsChange={selectedEndpoints => {
                this.setState({ selectedEndpoints });
              }}
              onReloadButtonClick={() => {
                this.handleReloadButtonClick();
              }}
              renderItemComponent={options => (
                <EndpointListItem
                  {...options}
                  getUsage={endpoint => this.getEndpointUsage(endpoint.id)}
                  getRegionName={id => this.regionName(id)}
                  check={this.state.checks[options.item.id]}
                  actions={this.rowActions(options.item)}
                />
              )}
              emptyListIcon={Cloud}
              emptyListMessage="No cloud endpoints in this project yet"
              emptyListExtraMessage="A cloud endpoint holds the connection to a source or target cloud: a Nutanix cluster, a VMware vCenter, an AWS or Azure account, an on-prem hub…"
              emptyListButtonLabel="Add endpoint"
              onEmptyListButtonClick={() => {
                this.handleEmptyListButtonClick();
              }}
              itemsPerPageOptions={[10, 25, 50]}
              initialItemsPerPage={10}
            />
          }
          headerComponent={
            <PageHeader
              title="Cloud endpoints"
              subtitle="The clouds and platforms SwiftIQ can move workloads between."
              onProjectChange={() => {
                this.handleProjectChange();
              }}
              onModalOpen={() => {
                this.handleModalOpen();
              }}
              onModalClose={() => {
                this.handleModalClose();
              }}
            />
          }
        />
        {this.state.showDeleteEndpointsModal ? (
          <AlertModal
            isOpen
            title="Delete endpoints?"
            message="Are you sure you want to delete the selected endpoints?"
            extraMessage="Deleting a SwiftIQ cloud endpoint is permanent!"
            onConfirmation={() => {
              this.deleteSelectedEndpoints();
            }}
            onRequestClose={() => {
              this.setState({ showDeleteEndpointsModal: false });
            }}
          />
        ) : null}
        <Modal
          isOpen={this.state.showChooseProviderModal}
          title="New cloud endpoint"
          onRequestClose={() => {
            this.handleCloseChooseProviderModal();
          }}
        >
          <ChooseProvider
            onCancelClick={() => {
              this.handleCloseChooseProviderModal();
            }}
            regions={regionStore.regions}
            providers={providerStore.providerNames}
            loading={providerStore.providersLoading || regionStore.loading}
            onUploadEndpoint={endpoint => {
              this.handleUploadEndpoint(endpoint);
            }}
            onProviderClick={providerName => {
              this.handleProviderClick(providerName);
            }}
            multiValidating={this.state.multiValidating}
            onValidateMultipleEndpoints={endpoints => {
              this.handleValidateMultipleEndpoints(endpoints);
            }}
            multiValidation={endpointStore.multiValidation}
            onRemoveEndpoint={e => {
              this.handleRemoveEndpoint(e);
            }}
            onResetValidation={() => {
              this.handleResetValidation();
            }}
          />
        </Modal>
        <Modal
          isOpen={this.state.showEndpointModal}
          title="New cloud endpoint"
          onRequestClose={() => {
            this.handleCloseEndpointModal();
          }}
        >
          <EndpointModal
            type={this.state.providerType}
            onCancelClick={() => {
              this.handleCloseEndpointModal();
            }}
            endpoint={this.state.uploadedEndpoint}
            isNewEndpoint={Boolean(this.state.uploadedEndpoint)}
          />
        </Modal>
        <AlertModal
          type="error"
          isOpen={this.state.showEndpointsInUseModal}
          title="Endpoints are in use"
          message="Some of the selected endpoints can't be deleted because transfers use them."
          extraMessage="Delete the transfers (replicas or migrations) that use these endpoints first."
          onRequestClose={() => {
            this.setState({ showEndpointsInUseModal: false });
          }}
        />
        {this.state.showDuplicateModal ? (
          <Modal
            isOpen
            title="Duplicate endpoint"
            onRequestClose={() => {
              this.setState({ showDuplicateModal: false });
            }}
          >
            <EndpointDuplicateOptions
              duplicating={this.state.duplicating}
              projects={projectStore.projects}
              selectedProjectId={selectedProjectId}
              onCancelClick={() => {
                this.setState({ showDuplicateModal: false });
              }}
              onDuplicateClick={projectId => {
                this.duplicate(projectId);
              }}
            />
          </Modal>
        ) : null}
      </Wrapper>
    );
  }
}

function EndpointsPageWithNavigate() {
  const navigate = useNavigate();
  const query = useListQuery();

  return <EndpointsPage onNavigate={navigate} query={query} />;
}

export default EndpointsPageWithNavigate;
